/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { RichMentionedUrl } from 'schemas/RichMentionedUrlSchema'
import { PostMetricWithSoundbites } from 'components/widget/PopularLinksWidgets/types'
import {
  getWidgetForDashboard,
  getMoreWidgetForDashboard,
  getDataForCustomVizWidget,
  getPrerequisiteWidgetForDashboard,
  getWidgetPostSoundbites
} from './asyncThunks'
import { WidgetSlice } from './types'

const initialState: Readonly<WidgetSlice> = {
  dashboardWidgets: {},
  analyzeWidget: {},
  status: 'idle',
  error: null
}

const widgetSlice = createSlice({
  name: 'widget',
  initialState,
  reducers: {
    setStatus: (state, action) => {
      state.dashboardWidgets[action.payload.widgetId] =
        state.dashboardWidgets[action.payload.widgetId] || {}
      state.dashboardWidgets[action.payload.widgetId].status = action.payload.status
    },
    setFailureMessage: (state, action) => {
      state.dashboardWidgets[action.payload.widgetId] =
        state.dashboardWidgets[action.payload.widgetId] || {}
      state.dashboardWidgets[action.payload.widgetId].failureMessage = action.payload.failureMessage
    },
    setLoadMoreCursor: (state, action) => {
      state.dashboardWidgets[action.payload.widgetId] =
        state.dashboardWidgets[action.payload.widgetId] || {}
      state.dashboardWidgets[action.payload.widgetId].loadMoreCursor = action.payload.loadMoreCursor
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(
        // Currently we're unable to change pending status within specific widget with 'widgetId'
        // since getWidgetForDashboard.pending action is fired before the payloadCreator is being called
        // https://redux-toolkit.js.org/api/createAsyncThunk#return-value
        getWidgetForDashboard.pending,
        (state, action) => {
          state.dashboardWidgets[action.meta.arg.widgetId] =
            state.dashboardWidgets[action.meta.arg.widgetId] || {}
          state.dashboardWidgets[action.meta.arg.widgetId].status = 'loading'
        }
      )
      .addCase(getWidgetForDashboard.fulfilled, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].status = 'succeeded'
        state.dashboardWidgets[action.meta.arg.widgetId].data = action.payload.results
        state.dashboardWidgets[action.meta.arg.widgetId].dataIdx = action.payload.resultsIdx
      })
      .addCase(getWidgetForDashboard.rejected, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].status = 'failed'
        if (action.payload) {
          state.dashboardWidgets[action.meta.arg.widgetId].error = action.payload
        } else {
          state.dashboardWidgets[action.meta.arg.widgetId].error = action.error
        }
      })
      .addCase(getPrerequisiteWidgetForDashboard.pending, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].preWorkStatus = 'loading'
        // * reset load more status and data
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = undefined
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreCursor = 0
        state.dashboardWidgets[action.meta.arg.widgetId].data = undefined
      })
      .addCase(getPrerequisiteWidgetForDashboard.fulfilled, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].preWorkStatus = 'succeeded'
        state.dashboardWidgets[action.meta.arg.widgetId].preWorkData = action.payload.results
      })
      .addCase(getPrerequisiteWidgetForDashboard.rejected, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].preWorkStatus = 'failed'
        if (action.payload) {
          state.dashboardWidgets[action.meta.arg.widgetId].error = action.payload
        } else {
          state.dashboardWidgets[action.meta.arg.widgetId].error = action.error
        }
      })
      .addCase(getMoreWidgetForDashboard.pending, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = 'loading'
      })
      .addCase(getMoreWidgetForDashboard.fulfilled, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = 'succeeded'
        // * append next page data to existing data
        state.dashboardWidgets[action.meta.arg.widgetId].data =
          state.dashboardWidgets[action.meta.arg.widgetId].data === undefined
            ? action.payload.results
            : [...state.dashboardWidgets[action.meta.arg.widgetId].data, ...action.payload.results]
      })
      .addCase(getMoreWidgetForDashboard.rejected, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = 'failed'
      })

      .addCase(getWidgetPostSoundbites.rejected, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = 'failed'
      })
      .addCase(getWidgetPostSoundbites.pending, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = 'loading'
      })
      .addCase(getWidgetPostSoundbites.fulfilled, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId].loadMoreStatus = 'succeeded'
        state.dashboardWidgets[action.meta.arg.widgetId].data[0].results[0].data.metrics?.forEach(
          (metric: PostMetricWithSoundbites) => {
            metric?.mentionedUrls?.forEach((mentionedUrl: RichMentionedUrl) => {
              if (mentionedUrl?.url === action.payload.url) {
                metric.soundbitesData = action.payload.results?.[0]?.results?.[0]?.data
                metric.soundbitesDataStatus = 'succeeded'
              }
            })
          }
        )
      })

      .addCase(getDataForCustomVizWidget.pending, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].status = 'loading'
      })
      .addCase(getDataForCustomVizWidget.fulfilled, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].status = 'succeeded'
        state.dashboardWidgets[action.meta.arg.widgetId].data = action.payload
      })
      .addCase(getDataForCustomVizWidget.rejected, (state, action) => {
        state.dashboardWidgets[action.meta.arg.widgetId] =
          state.dashboardWidgets[action.meta.arg.widgetId] || {}
        state.dashboardWidgets[action.meta.arg.widgetId].status = 'failed'
        state.dashboardWidgets[action.meta.arg.widgetId].error = action.payload
      })
})

export const { setStatus, setFailureMessage, setLoadMoreCursor } = widgetSlice.actions

export default widgetSlice.reducer
